import React, { useState, useEffect } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/avatar";
import { Button } from "src/components/ui/button";
import { Dialog, DialogContent } from "src/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { Badge } from "src/components/ui/badge";
import { Separator } from "src/components/ui/separator";
import { Card, CardContent } from "src/components/ui/card";
import {
  Link2,
  Paperclip,
  Eye,
  Download,
  ExternalLink,
  UserPlus,
  Check,
  User,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  File,
  Image as ImageIcon,
  Mic,
  Video as VideoIcon,
  X,
} from "lucide-react";
import dayjs from "dayjs";
import { apiRequests } from "src/shared/api/api";
import { buildPhotoUrl } from "./utils";
import ContragentChainModal from "./ContragentChainModal";

const resolveAttachmentUrl = (item) =>
  buildPhotoUrl(item?.url) || item?.url || null;

const getAttachmentMeta = (messageType) => {
  switch (String(messageType || "").toUpperCase()) {
    case "IMAGE":
      return { title: "Изображение", Icon: ImageIcon };
    case "VOICE":
      return { title: "Голосовое сообщение", Icon: Mic };
    case "VIDEO":
      return { title: "Видео", Icon: VideoIcon };
    case "DOCUMENT":
      return { title: "Файл", Icon: File };
    default:
      return { title: "Вложение", Icon: File };
  }
};

export default function ChatInfo({ selectedChat, formatTime, token }) {
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [chainModalVisible, setChainModalVisible] = useState(false);
  const [chaining] = useState(false);

  useEffect(() => {
    if (!selectedChat?.id) return;

    const loadAttachments = async () => {
      setLoadingAttachments(true);
      try {
        const response = await apiRequests.chats.getChatFiles(
          selectedChat.id,
          token ? { token } : {},
        );

        let files = [];
        if (Array.isArray(response.data)) {
          files = response.data;
        }

        setAttachments(files);
      } catch (error) {
        console.error("Ошибка загрузки вложений:", error);
        setAttachments([]);
      } finally {
        setLoadingAttachments(false);
      }
    };

    loadAttachments();
  }, [selectedChat?.id, token]);

  const imageAttachments = attachments.filter(
    (item) => String(item?.message_type || "").toUpperCase() === "IMAGE",
  );
  const otherAttachments = attachments.filter(
    (item) => String(item?.message_type || "").toUpperCase() !== "IMAGE",
  );

  return (
    <div className="p-4 h-full overflow-y-auto bg-background">
      {/* Заголовок с аватаром */}
      <div className="text-center mb-6 px-2">
        <Avatar className="w-20 h-20 mx-auto mb-3 border-2 border-border">
          <AvatarImage src={selectedChat?.contact?.avatar} />
          <AvatarFallback>
            <User className="w-10 h-10" />
          </AvatarFallback>
        </Avatar>
        <h4 className="text-lg font-semibold break-words mb-0">
          {selectedChat?.contact?.name || selectedChat?.name || "Чат"}
        </h4>
      </div>

      <Separator className="my-4" />

      {/* Привязанный контрагент */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <span className="text-sm font-medium">Привязанный контрагент</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setChainModalVisible(true)}
                  disabled={chaining}
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Привязать
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Привязать контрагента</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {selectedChat?.contact?.contragent_id ? (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm">Контрагент привязан</span>
                <Badge
                  variant="outline"
                  className="bg-green-100 text-green-800 border-green-300"
                >
                  ID: {selectedChat.contact.contragent_id}
                </Badge>
              </div>
              {selectedChat.contact.name && (
                <div className="mt-2">
                  <span className="text-xs text-muted-foreground">Имя:</span>
                  <span className="ml-2 text-sm">
                    {selectedChat.contact.name}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-amber-600" />
                <span className="text-sm">Контрагент не привязан</span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Привяжите контрагента для ведения истории и учета
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <Separator className="my-4" />

      {/* Контактная информация */}
      <div className="mb-6">
        <h5 className="font-medium text-sm mb-3">Контактная информация</h5>

        {selectedChat?.contact?.phone && (
          <div className="mb-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Phone className="w-3 h-3" />
              Телефон
            </div>
            <p className="text-sm">{selectedChat.contact.phone}</p>
          </div>
        )}

        {selectedChat?.contact?.email && (
          <div className="mb-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Mail className="w-3 h-3" />
              Email
            </div>
            <p className="text-sm">{selectedChat.contact.email}</p>
          </div>
        )}
      </div>

      <Separator className="my-4" />

      {/* Статистика */}
      <div className="mb-6">
        <h5 className="font-medium text-sm mb-3">Статистика</h5>

        <div className="mb-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <MessageSquare className="w-3 h-3" />
            Первое сообщение
          </div>
          <p className="text-sm">
            {selectedChat?.first_message_time
              ? dayjs(selectedChat.first_message_time).format(
                  "DD.MM.YYYY HH:mm",
                )
              : "Нет данных"}
          </p>
        </div>

        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
            <Calendar className="w-3 h-3" />
            Последнее сообщение
          </div>
          <p className="text-sm">
            {selectedChat?.last_message_time
              ? dayjs(selectedChat.last_message_time).format("DD.MM.YYYY HH:mm")
              : "Нет сообщений"}
          </p>
        </div>
      </div>

      <Separator className="my-4 p" />

      <div className="flex items-center gap-2 px-2 mb-4">
        <Paperclip className="w-4 h-4" />
        <span className="font-medium text-sm">
          Вложения ({attachments.length})
        </span>
      </div>

      {loadingAttachments ? (
        <div className="text-center py-6">
          <p className="text-muted-foreground text-sm">Загрузка вложений...</p>
        </div>
      ) : attachments.length === 0 ? (
        <div className="text-center py-8">
          <File className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">Нет вложений</p>
        </div>
      ) : (
        <div className="mt-4">
          {imageAttachments.length > 0 && (
            <>
              <h5 className="font-medium text-sm mb-3">
                Фотографии ({imageAttachments.length})
              </h5>

              <div className="grid grid-cols-3 gap-2 mb-6">
                {imageAttachments.map((item, index) => {
                  const imageSrc = resolveAttachmentUrl(item);
                  return (
                    <div
                      key={item.message_id || index}
                      className="relative aspect-square rounded-md overflow-hidden border border-border bg-muted cursor-pointer group"
                      onClick={() => {
                        setPreviewImage(imageSrc);
                        setPreviewVisible(true);
                      }}
                    >
                      <img
                        src={imageSrc}
                        alt={`Фото ${index + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentNode.style.backgroundColor = "#f5f5f5";
                        }}
                      />
                      <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye className="w-5 h-5 text-white" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {otherAttachments.length > 0 && (
            <>
              <h5 className="font-medium text-sm mb-3">
                Файлы ({otherAttachments.length})
              </h5>

              <div className="space-y-3">
                {otherAttachments.map((item) => {
                  const attachmentUrl = resolveAttachmentUrl(item);
                  const messageType = String(
                    item?.message_type || "",
                  ).toUpperCase();
                  const { title, Icon } = getAttachmentMeta(messageType);

                  return (
                    <Card
                      key={item.message_id}
                      className="border border-border shadow-none"
                    >
                      <CardContent className="p-3 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-2 min-w-0">
                            <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium break-words">
                                {title}
                              </div>
                              {item.preview_text && (
                                <div className="text-xs text-muted-foreground mt-1 break-all">
                                  {item.preview_text}
                                </div>
                              )}
                              <div className="text-xs text-muted-foreground mt-1">
                                {item.created_at
                                  ? dayjs(item.created_at).format(
                                      "DD.MM.YYYY HH:mm",
                                    )
                                  : "Без даты"}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() =>
                                window.open(
                                  attachmentUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => {
                                const link = document.createElement("a");
                                link.href = attachmentUrl;
                                link.download = `${messageType.toLowerCase()}_${item.message_id}`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        {messageType === "VOICE" && attachmentUrl && (
                          <audio
                            controls
                            preload="metadata"
                            src={attachmentUrl}
                            className="w-full"
                          />
                        )}

                        {messageType === "VIDEO" && attachmentUrl && (
                          <video
                            controls
                            preload="metadata"
                            src={attachmentUrl}
                            className="w-full rounded-md bg-black"
                          />
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Модальное окно для просмотра изображения */}
      <Dialog open={previewVisible} onOpenChange={setPreviewVisible}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 border-none bg-transparent shadow-none">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Кнопка закрытия - справа сверху */}
            <Button
              size="icon"
              variant="secondary"
              className="absolute top-2 right-16 z-50 bg-background/30 backdrop-blur-sm hover:bg-background/70"
              onClick={() => setPreviewVisible(false)}
            >
              <X className="h-4 w-4" />
            </Button>

            {/* Изображение */}
            <img
              src={previewImage}
              alt="Превью"
              className="max-w-full max-h-full object-contain"
            />

            {/* Кнопка скачать - справа снизу */}
            <div className="absolute bottom-2 right-16 z-50">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      className="bg-background/30 backdrop-blur-sm hover:bg-background/70"
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = previewImage;
                        link.download = `attachment_${Date.now()}.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Скачать</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ContragentChainModal
        visible={chainModalVisible}
        onCancel={() => setChainModalVisible(false)}
        onSuccess={(contragent) => {
          if (selectedChat) {
            selectedChat.contact = {
              ...selectedChat.contact,
              contragent_id: contragent.id,
              name: contragent.name,
              phone: contragent.phone,
            };
          }
        }}
        chatId={selectedChat?.id}
        currentContragentId={selectedChat?.contact?.contragent_id}
      />
    </div>
  );
}
